import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Robot, ChatCircleDots, X, PaperPlaneTilt, ArrowRight, Microphone, Stop, ArrowsOut } from "@phosphor-icons/react";

const STORAGE_KEY = "iaam_punch_agent_key";
const FALLBACK_AGENT = "punch-ai";

const ROUTE_SUGGESTIONS = [
  { match: /^\/feed/,            chips: ["What's trending in mobility today?", "Find me an EV-corridor insight"] },
  { match: /^\/hub\/ev/,         chips: ["Brief me on EV fleet ops", "What's the dispatch vector look like?"] },
  { match: /^\/hub\/automotive/, chips: ["Run a fleet status check", "Predict maintenance windows"] },
  { match: /^\/hub\/uam/,        chips: ["Vertiport siting risks?", "eVTOL noise corridor analysis"] },
  { match: /^\/hub\/aerospace/,  chips: ["Hybrid-electric regional viability?", "SAF supply bottlenecks"] },
  { match: /^\/economy/,         chips: ["Top 3 optimization opportunities?", "Show me anomaly KPIs"] },
  { match: /^\/agents/,          chips: ["Recommend an agent for dispatch", "Deploy the right sub-agent for me"] },
  { match: /^\/orgs?/,           chips: ["Onboard a new mobility business", "Build me an AI adoption roadmap"] },
  { match: /^\/live/,            chips: ["Summarize today's live sessions", "Schedule a fleet-ops briefing"] },
];
const DEFAULT_CHIPS = [
  "Brief me on iAAM.one",
  "What's PUNCH AI good at?",
  "Show me an operational playbook",
];

/**
 * ChatbotWidget — floating site-wide PUNCH AI chatbot.
 * Lives in the bottom-right on every authenticated route.
 */
export default function ChatbotWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [agent, setAgent] = useState(null);
  const [agentsAll, setAgentsAll] = useState([]);
  const [agentKey, setAgentKey] = useState(() => localStorage.getItem(STORAGE_KEY) || FALLBACK_AGENT);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceState, setVoiceState] = useState("idle"); // idle | recording | processing
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const audioPlayerRef = useRef(null);

  // Load agents list
  useEffect(() => {
    if (!open) return;
    api.get("/agents").then((r) => setAgentsAll(r.data || [])).catch(() => {});
  }, [open]);

  // Load selected agent + history
  useEffect(() => {
    if (!open || !agentKey) return;
    let cancel = false;
    (async () => {
      try {
        const { data: a } = await api.get(`/agents/${agentKey}`);
        if (cancel) return;
        setAgent(a);
        if (user && user.id) {
          const { data: h } = await api.get(`/agents/${agentKey}/chat/history`);
          if (!cancel) setMessages(h || []);
        } else {
          setMessages([]);
        }
      } catch (_) {}
    })();
    return () => { cancel = true; };
  }, [agentKey, open, user]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const switchAgent = (k) => {
    setAgentKey(k);
    localStorage.setItem(STORAGE_KEY, k);
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy || !agent) return;
    setBusy(true);
    setInput("");
    setMessages((m) => [...m, { id: `t-${Date.now()}`, role: "user", text, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post(`/agents/${agentKey}/chat`, { message: text });
      setMessages((m) => [...m.filter((x) => !String(x.id).startsWith("t-")), data.user_message, data.agent_message]);
    } catch (err) {
      setMessages((m) => [...m.filter((x) => !String(x.id).startsWith("t-")), { id: `err-${Date.now()}`, role: "agent", text: "⚠️  Could not reach the agent. Try again?", created_at: new Date().toISOString() }]);
    } finally { setBusy(false); }
  };

  const toggleVoice = async () => {
    if (voiceState === "recording") {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setVoiceState("processing");
        const blob = new Blob(chunks, { type: "audio/webm" });
        const fd = new FormData();
        fd.append("audio", new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" }));
        try {
          const { data } = await api.post(`/agents/${agentKey}/voice-chat`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setMessages((m) => [...m, data.user_message, data.agent_message]);
          if (data.audio_url && audioPlayerRef.current) {
            const src = data.audio_url.startsWith("/api/")
              ? `${process.env.REACT_APP_BACKEND_URL}${data.audio_url}`
              : data.audio_url;
            audioPlayerRef.current.src = src;
            audioPlayerRef.current.play().catch(() => {});
          }
        } catch (e) {
          setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "agent", text: "⚠️  Voice request failed.", created_at: new Date().toISOString() }]);
        } finally { setVoiceState("idle"); }
      };
      rec.start();
      recorderRef.current = rec;
      setVoiceState("recording");
    } catch (e) {
      alert("Microphone permission required.");
    }
  };

  const avatarUrl = agent?.avatar_url
    ? (agent.avatar_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${agent.avatar_url}` : agent.avatar_url)
    : null;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed right-5 z-40 w-14 h-14 rounded-full bg-[#E60012] hover:bg-[#E60012] flex items-center justify-center shadow-2xl transition-transform hover:scale-105"
        style={{ bottom: "5rem", boxShadow: "0 10px 40px rgba(230,0,18,0.4)" }}
        data-testid="chatbot-fab"
        aria-label="Open chat"
      >
        <ChatCircleDots size={24} weight="fill" className="text-white" />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#00E676] rounded-full pulse border-2 border-[#0A0A0C]" />
      </button>
    );
  }

  return (
    <>
      <audio ref={audioPlayerRef} className="hidden" autoPlay />
      <div className="fixed right-5 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-7rem)] flex flex-col card overflow-hidden" data-testid="chatbot-panel"
        style={{ bottom: "5rem", boxShadow: "0 24px 80px rgba(0,0,0,0.5)" }}>
        {/* Header */}
        <div className="px-3 py-2 border-b border-[#27272A] flex items-center gap-2 bg-[#121214]">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover border border-[#27272A]" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-[#E60012] flex items-center justify-center"><Robot size={14} weight="fill" className="text-white" /></div>
            )}
            <div className="min-w-0">
              <div className="text-sm text-white truncate">{agent?.name || "PUNCH AI"}</div>
              <select
                value={agentKey}
                onChange={(e) => switchAgent(e.target.value)}
                className="bg-transparent text-zinc-400 label-mono cursor-pointer outline-none -ml-1"
                style={{ fontSize: 9 }}
                data-testid="chatbot-agent-select"
              >
                {agentsAll.length === 0 && <option value={agentKey}>LOADING…</option>}
                {agentsAll.map((a) => (
                  <option key={a.key} value={a.key} style={{ background: "#0A0A0C" }}>
                    {`${a.role.replace(" Agent", "").toUpperCase()} · ${a.name}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Link to={agent ? `/agent/${agent.key}` : "/agents"} className="btn btn-ghost btn-sm" title="Open full chat" data-testid="chatbot-expand">
            <ArrowsOut size={12} />
          </Link>
          <button onClick={() => setOpen(false)} className="btn btn-ghost btn-sm" data-testid="chatbot-close" aria-label="Close">
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="chatbot-scroll">
          {!user || !user.id ? (
            <div className="text-center text-zinc-400 py-8">
              <Robot size={28} weight="light" className="mx-auto mb-3 text-zinc-600" />
              <p className="text-sm mb-3">Sign in to chat with iAAM agents.</p>
              <Link to="/login" className="btn btn-primary btn-sm">Sign in</Link>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-zinc-500 py-6" data-testid="chatbot-empty">
              <div className="label-mono mb-1" style={{ color: agent?.color || "#E60012" }}>CHANNEL OPEN</div>
              <p className="text-xs mb-4">Ask {agent?.name || "the agent"} anything.</p>
              <div className="flex flex-wrap gap-1.5 justify-center px-2">
                {(ROUTE_SUGGESTIONS.find((r) => r.match.test(location.pathname))?.chips || DEFAULT_CHIPS).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setInput(c)}
                    className="text-xs border border-[#27272A] hover:border-[#E60012] hover:text-white text-zinc-400 px-2.5 py-1 transition-colors"
                    data-testid={`chatbot-suggestion-${c.slice(0, 18).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          ) : messages.map((m) => (
            <div key={m.id} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              <div className="shrink-0">
                {m.role === "agent" ? (
                  avatarUrl ? <img src={avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover border border-[#27272A]" /> : <div className="w-7 h-7 rounded-full" style={{ background: agent?.color || "#E60012" }} />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#E60012] flex items-center justify-center font-display text-xs text-white">{user?.name?.[0]}</div>
                )}
              </div>
              <div className={`max-w-[80%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "agent" ? "bg-[#121214] border border-[#27272A] text-zinc-100" : "bg-[#E60012] text-white"
              }`}>{m.text}</div>
            </div>
          ))}
          {(busy || voiceState === "processing") && (
            <div className="flex gap-2" data-testid="chatbot-typing">
              <div className="w-7 h-7 rounded-full" style={{ background: agent?.color || "#E60012" }} />
              <div className="px-3 py-2 bg-[#121214] border border-[#27272A] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full pulse" style={{ background: "#fff" }} />
                <span className="w-1.5 h-1.5 rounded-full pulse" style={{ background: "#fff", animationDelay: "200ms" }} />
                <span className="w-1.5 h-1.5 rounded-full pulse" style={{ background: "#fff", animationDelay: "400ms" }} />
              </div>
            </div>
          )}
        </div>

        {/* Input bar */}
        <form onSubmit={send} className="p-2 border-t border-[#27272A] flex gap-1.5" data-testid="chatbot-form">
          <button
            type="button"
            onClick={toggleVoice}
            disabled={!user || !user.id || voiceState === "processing"}
            className={`btn btn-sm ${voiceState === "recording" ? "btn-primary" : ""}`}
            title={voiceState === "recording" ? "Stop & send" : "Push to talk"}
            data-testid="chatbot-voice-btn"
          >
            {voiceState === "recording" ? <Stop size={12} weight="fill" /> : <Microphone size={12} weight="bold" />}
          </button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={!user || !user.id ? "Sign in to chat" : (voiceState === "recording" ? "Recording... tap stop to send" : `Ask ${agent?.name || "agent"}...`)}
            disabled={!user || !user.id || busy || voiceState !== "idle"}
            className="input py-1.5 text-sm flex-1"
            data-testid="chatbot-input"
          />
          <button type="submit" disabled={!input.trim() || busy || !user || !user.id} className="btn btn-primary btn-sm" data-testid="chatbot-send">
            <PaperPlaneTilt size={12} weight="bold" />
          </button>
        </form>
      </div>
    </>
  );
}
